import type { AuthUser } from '@/types/auth/authTypes';
import { formatDate } from '@/shared/formatters';
import { ehAdminDoEscritorio } from '@/shared/utils/papelEscritorio';

import { DIAS_DE_SOBREVIDA, TAMANHO_MAXIMO_DO_NOME } from '../constants/chavesDeApi';
import type {
  ChaveDoCliente,
  DadosDaChaveNova,
  DadosDaRotacao,
  ProblemaDoFormulario,
} from '../types/chavesDeApiTypes';

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * D10-5, espelho de `acessoAsChaves.ts` do core: administra as chaves o admin-master da Arbitralis
 * (`tipoUsuario = ADMIN`, mesmo sem ser membro, por isso o papel no escritório não basta) ou quem
 * é `MASTER_ADMIN`/`ADMIN` do escritório. A secretaria da Arbitralis (`tipoUsuario = SECRETARIA`)
 * fica de fora **mesmo com papel de admin no escritório**: o core a recusa nesse ramo, e abrir a
 * tela só a levaria a um 403 em toda ação.
 */
export function podeAdministrarChaves(
  user: Pick<AuthUser, 'tipoUsuario' | 'papelEscritorio'> | null | undefined,
): boolean {
  if (!user) return false;
  if (user.tipoUsuario === 'ADMIN') return true;
  return user.tipoUsuario !== 'SECRETARIA' && ehAdminDoEscritorio(user.papelEscritorio);
}

/** Chaves antigas não têm nome: o prefixo é o que o suporte e o cliente usam para achá-las. */
export const rotuloDaChave = (chave: Pick<ChaveDoCliente, 'nome' | 'keyPrefix'>): string =>
  chave.nome?.trim() ? chave.nome : chave.keyPrefix;

/**
 * D10-1: no máximo uma `VIGENTE`. A chave em sobrevida não conta — é justamente o que deixa a
 * rotação trocar a chave sem derrubar a integração.
 */
export const temChaveVigente = (chaves: readonly ChaveDoCliente[]): boolean =>
  chaves.some((chave) => chave.estado === 'VIGENTE');

export const podeRotacionar = (chave: Pick<ChaveDoCliente, 'estado'>): boolean =>
  chave.estado === 'VIGENTE';

/** Revogar serve à vigente e também à antiga em sobrevida, para encurtar os 30 dias. */
export const podeRevogar = (chave: Pick<ChaveDoCliente, 'estado'>): boolean =>
  chave.estado === 'VIGENTE' || chave.estado === 'EM_SOBREVIDA';

/**
 * Data em que a chave antiga deixa de valer se a rotação for confirmada agora. O core é quem fixa
 * o prazo (D10-2); a tela só antecipa a data para a confirmação. `agora` é parâmetro para o teste
 * não depender do relógio.
 */
export const fimDaSobrevidaSeRotacionarAgora = (agora: Date = new Date()): string =>
  formatDate(new Date(agora.getTime() + DIAS_DE_SOBREVIDA * MS_POR_DIA));

/**
 * Na chave em sobrevida, o fim é o `revogadaEm` — uma data futura, gravada pela rotação. Nos
 * outros estados não há "expira em" a mostrar.
 */
export const fimDaSobrevida = (
  chave: Pick<ChaveDoCliente, 'estado' | 'revogadaEm'>,
): string | null =>
  chave.estado === 'EM_SOBREVIDA' && chave.revogadaEm ? formatDate(chave.revogadaEm) : null;

/**
 * As mesmas regras do Zod do core (R1.2 e R4.1), para o botão só habilitar o que o core aceita.
 * A rotação não tem escopos a conferir: o core copia os da chave antiga.
 */
export function problemaDoFormulario(
  dados: DadosDaChaveNova | DadosDaRotacao,
): ProblemaDoFormulario | null {
  const nome = dados.nome.trim();
  if (nome.length === 0) return 'NOME_VAZIO';
  if (nome.length > TAMANHO_MAXIMO_DO_NOME) return 'NOME_LONGO';
  if ('escopos' in dados && dados.escopos.length === 0) return 'SEM_ESCOPO';
  return null;
}
